import React, { useState, useEffect } from "react";
import Wrp from '../../../hoc/Wrp/Wrp';
import Icons from "../../../Components/Icons/Icons";
import Modal from "react-bootstrap/Modal";
import Inputs from '../../../Components/Inputs/Inputs';
import Pagination from "../../../Components/UI/TableRecord/Pagination";
import { getUserInfo } from '../../../Utils/UserFunctions';
import { isSet, roundOffTwoDecimal } from '../../../Utils/CommonFunctions';
import { callGET } from '../../../Utils/ApiUtil';
import TableData from '../../../Components/UI/TableRecord/TableData/index';

const DirectoryMapping = (props) => {
  const { documentList, isModalOpen, toggleDirectory, documentValueHandler, documentValue } = props;

  // State for selected value (to be saved)
  const [directoryValue, setDirectoryValue] = useState({
    name: '',
    value: '',
    size: ''
  });
  const [isDirectoryValue, setIsDirectoryValue] = useState(true);

  // State for currently viewed folder metadata
  const [currentFolder, setCurrentFolder] = useState(null);

  // State for folder content (fetched from API)
  const [folderContent, setFolderContent] = useState([]);

  // Pagination State
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0
  });

  // Initialize current folder on mount
  // useEffect(() => {
  //   if (documentList && documentList.length > 0 && !currentFolder) {
  //     const firstDir = documentList[0];
  //     setCurrentFolder(firstDir);
  //     // Fetch data for the first directory
  //     fetchDirectoryData(firstDir.id, { ...pagination, page: 0 });
  //   }
  // }, [documentList]);

  // Sync selection state with props when modal opens
  useEffect(() => {
    if (isModalOpen) {
      if (documentValue && documentValue.value) {
        setDirectoryValue({
          name: documentValue.name,
          value: documentValue.value,
          size: documentValue.size
        });
        setIsDirectoryValue(documentValue.is_directory);
      } else {
        setDirectoryValue({
          name: '',
          value: '',
          size: ''
        });
        setIsDirectoryValue(true);
      }
    }
  }, [isModalOpen, documentValue]);

  // Fetch data from API
  const fetchDirectoryData = async (directoryId, pageParams) => {
    if (!directoryId) return;
    props.Loader(true)
    try {
      const { page, perPage } = pageParams;
      // Construct query parameters similar to MainContent/index.js
      // Note: page * perPage is the offset if the API expects offset, or just page number?
      // MainContent uses: page=${page * perPage} which implies offset.
      // Let's verify MainContent: page=${page * perPage}&orderBy=${orderBy}&size=${perPage}
      // Yes, it sends offset as 'page'.

      const offset = page * perPage;
      const queryParams = `?directory_id=${directoryId}&page=${offset}&size=${perPage}`;

      const res = await callGET(`document/search${queryParams}`);

      if (res.status === 200 && res.data.code === 200) {
        const responseData = res.data.data;
        if (responseData && responseData.length > 0) {
          setFolderContent(responseData[0].documents || []);
          setPagination(prev => ({
            ...prev,
            page: pageParams.page,
            perPage: pageParams.perPage,
            totalRecords: responseData[0].count || 0
          }));
        } else {
          setFolderContent([]);
          setPagination(prev => ({
            ...prev,
            page: pageParams.page,
            perPage: pageParams.perPage,
            totalRecords: 0
          }));
        }
      } else {
        setFolderContent([]);
      }
    } catch (error) {
      console.error("Error fetching directory data:", error);
      setFolderContent([]);
    } finally {
      props.Loader(false)
    }
  };

  // Handler for clicking a directory in the tree (Left Panel)
  const handleDirectoryClick = (folder) => {
    if (currentFolder && currentFolder.id === folder.id) return;

    setCurrentFolder(folder);
    // Reset to page 0 when switching directories
    const newPagination = { ...pagination, page: 0 };
    fetchDirectoryData(folder.id, newPagination);
  };

  // Handler for selecting an item (Radio button)
  const setDirectoryHandler = (d) => {
    setDirectoryValue({
      name: d.name,
      value: d.id,
      size: d.size
    });
    if (d.type !== null && d.type !== undefined) {
      setIsDirectoryValue(false);
    } else {
      setIsDirectoryValue(true);
    }
  };

  // Pagination Change Handler
  const handlePaginationChange = (paginationData, shouldUpdate) => {
    if (shouldUpdate) {
      // Update state and fetch new data
      const newPagination = {
        ...pagination,
        page: paginationData.page,
        perPage: paginationData.perPage
      };

      // Only fetch if we have a current folder
      if (currentFolder) {
        fetchDirectoryData(currentFolder.id, newPagination);
      } else {
        setPagination(newPagination);
      }
    }
  };

  const saveValueHandler = () => {
    let updateValue = {
      name: directoryValue.name,
      value: directoryValue.value,
      size: directoryValue.size,
      is_directory: isDirectoryValue,
    }
    documentValueHandler(updateValue);
  }

  // Recursive Directory Tree Component (Left Panel)
  const DirectoryTree = ({ data, level = 0 }) => {
    return (
      <ul className="list-unstyled mb-0">
        {data.map((d, index) => {
          if (d.id !== '') {
            const isActive = currentFolder && currentFolder.id === d.id;
            const hasChildren = d.documents && d.documents.some(sub => !sub.type);

            return (
              <li key={index}>
                <div
                  className={`d-flex align-items-center py-1 ${isActive ? 'bg-light text-primary' : ''}`}
                  style={{ paddingLeft: `${level * 20 + 10}px`, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDirectoryClick(d);
                  }}
                >
                  <span className="mr-2 text-warning">
                    <Icons iconType="Folder" />
                  </span>
                  <span className={isActive ? 'font-weight-bold' : ''}>{d.name}</span>
                </div>
                {hasChildren && (
                  <DirectoryTree data={d.documents.filter(item => !item.type)} level={level + 1} />
                )}
              </li>
            );
          }
          return null;
        })}
      </ul>
    );
  };

  // Right Panel Content
  const renderRightPanel = () => {
    if (!currentFolder) return <div className="p-3 text-center">Select a directory</div>;

    return (
      <div className="table_Box dataTables_wrapper">
        <div className="table_Body table-scroll-2 list_View p-0" style={{ overflow: "auto", minHeight: '300px', maxHeight: '300px' }}>
          <table className="table mb-0">
            <thead className="table_tHeadBg">
              <tr>
                <th style={{ width: '30px' }}></th>
                <th>Name</th>
                <th>Type</th>
                <th>Size</th>
              </tr>
            </thead>
            <tbody>
              {folderContent.length > 0 ? (
                folderContent.map((item, index) => {
                  let checkedVal = directoryValue.value === item.id;
                  const isFolder = !item.type;

                  return (
                    <tr key={index} onClick={() => !isFolder ? setDirectoryHandler(item) : handleDirectoryClick(item)}>
                      <td>
                        {
                          !isFolder &&
                          <label className="form-radio-label">
                            <input
                              checked={checkedVal}
                              onChange={() => setDirectoryHandler(item)}
                              className="form-radio-input"
                              type="radio"
                              name="optionsRadios"
                            />
                          </label>
                        }
                      </td>
                      <td>
                        <span className="mr-2">
                          <i className={isFolder ? "fas fa-folder text-success" : "fas fa-file text-success"}></i>
                        </span>
                        {item.name}
                      </td>
                      <td>{isFolder ? 'Folder' : 'File'}</td>
                      <td>{isFolder ? '' : `${roundOffTwoDecimal(item.size/1000)} KB`}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="3" className="text-center">No items found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="table_Footer">
          {pagination.totalRecords > 0 ? (
            <Pagination
              {...pagination}
              recordsChangeHandler={handlePaginationChange}
            />
          ) : ''}
        </div>
      </div>
    );
  };

  return (
    <Modal dialogClassName="modal-dialog modal-dialog modal-xl height-auto" show={isModalOpen} onHide={toggleDirectory}  backdrop="static" keyboard={false}>
      <Modal.Header>
        <h5 className="modal-title secondary_color pl-2 mb-0"> Upload Files </h5>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-12">
            <div className="card card-transparent">
              <div className="card-header p-0">
                <div className="card-title">
                  Choose Directory or Files
                </div>
              </div>
              <div className="card-body p-0">
                <div className="row no-gutters">
                  {/* Left Section: Directory Tree */}
                  <div className="col-md-4 " style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <div className="p-2">
                      <h6 className="text-muted mb-3">Directories</h6>
                      <DirectoryTree data={documentList} />
                    </div>
                  </div>

                  {/* Right Section: Files List */}
                  <div className="col-md-8 border">
                    <div className="p-2">
                      <h6 className="text-muted mb-3">
                        <strong>{currentFolder ? currentFolder.name : ''}</strong>
                      </h6>
                      {renderRightPanel()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <a className="btn btn-border border-default mr-2" data-dismiss="modal" onClick={toggleDirectory}> Cancel </a>
        <a className="btn primary_bg" onClick={saveValueHandler}>Save</a>
      </Modal.Footer>
    </Modal>
  )
}

export default DirectoryMapping;